import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { IProductSection } from 'pages/ProductListing/types';
import {
  addQueryParamsWithoutReload,
  updateQueryParamsWithoutReload,
} from 'modules/main/utils';
import { ISortBy } from 'commerce/factFinder/queryParameters';
import { FilterClient } from 'commerce/factFinder/types';
import { IAdaptedListProduct } from 'commerce/scayle/utils/adapter/types';
import { getPageBanners } from 'commerce/services/banner';
import { GridBanner } from 'commerce/services/types';
import {
  getCategoryProductsClient,
  getLongTailProductsClient,
} from 'commerce/shop/client';

interface IProps {
  categoryName?: string;
  lastPage: number;
  initProducts: IAdaptedListProduct[];
  initBanners?: GridBanner[];
  withBanners?: boolean;
  page: number;
}

export const useProductSections = ({
  page,
  initProducts,
  categoryName,
  lastPage,
  initBanners,
  withBanners,
}: IProps) => {
  const [pagination, setPagination] = useState<{
    initSection: number;
    currentSection: number;
    lastPage: number;
  }>({
    initSection: page,
    currentSection: page,
    lastPage,
  });
  const pathname = usePathname();

  const [productSections, setProductSections] = useState<IProductSection[]>([
    {
      page,
      products: initProducts,
      banners: withBanners ? initBanners : [],
    },
  ]);

  const pushProductSection = async (
    page: number,
    filters: FilterClient,
    sortBy: ISortBy,
    query?: string,
  ) => {
    if (!query) {
      addQueryParamsWithoutReload({
        page,
        categoryName,
        filters,
        sortBy,
      });
    }

    const { data } = query
      ? await getLongTailProductsClient({
          page,
          filters,
          sortBy,
          query,
        })
      : await getCategoryProductsClient({
          page,
          categoryName,
          filters,
          sortBy,
        });

    const bannersResp = await getPageBanners({
      slug: pathname === '/' ? 'home' : pathname,
      idx: page,
    });
    const newBanners = bannersResp.response || [];

    setProductSections((prevState) => [
      ...prevState,
      {
        page,
        products: data?.hits || [],
        banners: withBanners ? newBanners : [],
      },
    ]);
  };

  const unshiftProductSection = async (
    page: number,
    filters: FilterClient,
    sortBy: ISortBy,
  ) => {
    const params = {
      page,
      categoryName,
      sortBy,
      filters,
    };

    addQueryParamsWithoutReload(params);
    const { data } = await getCategoryProductsClient(params);

    setProductSections((prevState) => [
      {
        page,
        products: data?.hits || [],
      },
      ...prevState,
    ]);
  };

  const setCurrentSection = ({
    page,
    lastPage,
  }: {
    page?: number;
    lastPage?: number;
  }) => {
    setPagination((s) => {
      if (!!page && page !== s.currentSection) {
        updateQueryParamsWithoutReload({ page: String(page) });
      }

      return {
        ...s,
        currentSection: page || s.currentSection,
        lastPage: lastPage || s.lastPage,
      };
    });
  };

  const isLoadMoreNextAvailable =
    pagination.lastPage > pagination.currentSection;

  const isLoadMorePrevAvailable = page !== 1 && pagination.currentSection !== 1;

  return {
    productSections,
    setProductSections,
    pushProductSection,
    unshiftProductSection,
    setCurrentSection,
    isLoadMoreNextAvailable,
    isLoadMorePrevAvailable,
  };
};
